import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { clearConsent, getConsent, setConsent } from './metaPixelConsent';
import { initMetaPixel, revokeMetaPixel } from './metaPixelLoader';
import {
  trackAddPaymentInfo,
  trackAddToCart,
  trackInitiateCheckout,
  trackPageView,
  trackPurchase,
  trackViewContent,
} from './metaPixelEvents';
import { resolveEffectiveMetaPixel } from './resolveEffectiveMetaPixel';
import type { MetaConsentDecision, MetaPixelConfig } from './metaPixelTypes';

type Options = {
  eventSlug?: string;
  eventId?: string;
  eventTitle?: string;
  metaPixel?: MetaPixelConfig | null;
  /** Dispara InitiateCheckout no mount (página de inscrição). */
  trackCheckoutOnMount?: boolean;
  /** Dispara ViewContent + PageView no mount (página pública). */
  trackViewOnMount?: boolean;
  /** Evento de teste não entra na campanha. */
  disabled?: boolean;
};

type TrackCommerceParams = {
  value?: number;
  contentIds?: string[];
  contentName?: string;
  numItems?: number;
  eventId?: string;
};

export function useEventMetaPixel(opts: Options) {
  const {
    eventSlug,
    eventId,
    eventTitle,
    metaPixel,
    trackCheckoutOnMount = false,
    trackViewOnMount = false,
    disabled = false,
  } = opts;

  const effective = useMemo(() => resolveEffectiveMetaPixel(metaPixel), [metaPixel]);
  const [consent, setConsentState] = useState<MetaConsentDecision | null>(() =>
    eventSlug ? getConsent(eventSlug)?.decision ?? null : null
  );
  const [bannerForced, setBannerForced] = useState(false);
  const [ready, setReady] = useState(false);
  const bootedKey = useRef('');

  useEffect(() => {
    if (!eventSlug) {
      setConsentState(null);
      return;
    }
    setConsentState(getConsent(eventSlug)?.decision ?? null);
  }, [eventSlug]);

  const showBanner = Boolean(effective && eventSlug && (consent === null || bannerForced));

  useEffect(() => {
    let cancelled = false;
    async function boot() {
      setReady(false);
      if (!effective || !eventSlug || consent === 'rejected' || disabled) return;
      try {
        await initMetaPixel(effective.pixelId);
        if (cancelled) return;
        const key = `${eventSlug}:${effective.pixelId}`;
        if (bootedKey.current !== key) {
          bootedKey.current = key;
          if (trackViewOnMount || trackCheckoutOnMount) {
            trackPageView();
          }
          if (trackViewOnMount) {
            trackViewContent({
              contentName: eventTitle,
              contentIds: eventId ? [eventId] : undefined,
            });
          }
          if (trackCheckoutOnMount) {
            trackInitiateCheckout({
              contentIds: eventId ? [eventId] : undefined,
            });
          }
        }
        setReady(true);
      } catch (err) {
        console.warn('[meta-pixel] falha ao inicializar:', err);
      }
    }
    void boot();
    return () => {
      cancelled = true;
    };
  }, [
    effective,
    eventSlug,
    consent,
    disabled,
    trackViewOnMount,
    trackCheckoutOnMount,
    eventId,
    eventTitle,
  ]);

  const accept = useCallback(() => {
    if (!eventSlug) return;
    setConsent(eventSlug, 'accepted');
    setConsentState('accepted');
    setBannerForced(false);
  }, [eventSlug]);

  const reject = useCallback(() => {
    if (!eventSlug) return;
    setConsent(eventSlug, 'rejected');
    setConsentState('rejected');
    setBannerForced(false);
    revokeMetaPixel();
    setReady(false);
  }, [eventSlug]);

  const manageCookies = useCallback(() => {
    if (!eventSlug) return;
    clearConsent(eventSlug);
    setConsentState(null);
    setBannerForced(true);
  }, [eventSlug]);

  const trackingAllowed = useCallback(() => {
    if (!ready || !eventSlug || disabled) return false;
    return getConsent(eventSlug)?.decision !== 'rejected';
  }, [ready, eventSlug, disabled]);

  const resolveContentIds = useCallback(
    (extra?: string[]) => {
      const ids = [...(eventId ? [eventId] : []), ...(extra || [])].filter(Boolean);
      return ids.length ? Array.from(new Set(ids.map(String))) : undefined;
    },
    [eventId]
  );

  const addToCart = useCallback(
    (params?: TrackCommerceParams) => {
      if (!trackingAllowed()) return;
      trackAddToCart({
        value: params?.value,
        contentName: params?.contentName || eventTitle,
        contentIds: resolveContentIds(params?.contentIds),
        numItems: params?.numItems,
      });
    },
    [trackingAllowed, eventTitle, resolveContentIds]
  );

  const addPaymentInfo = useCallback(
    (params?: TrackCommerceParams) => {
      if (!trackingAllowed()) return;
      trackAddPaymentInfo({
        value: params?.value,
        contentIds: resolveContentIds(params?.contentIds),
      });
    },
    [trackingAllowed, resolveContentIds]
  );

  const purchase = useCallback(
    (value: number, params?: Omit<TrackCommerceParams, 'value'>) => {
      if (!trackingAllowed()) return;
      trackPurchase({
        value,
        contentIds: resolveContentIds(params?.contentIds),
        numItems: params?.numItems,
        eventId: params?.eventId,
      });
    },
    [trackingAllowed, resolveContentIds]
  );

  return {
    effectivePixel: effective,
    showBanner,
    consent,
    ready,
    accept,
    reject,
    manageCookies,
    trackAddToCart: addToCart,
    trackAddPaymentInfo: addPaymentInfo,
    trackPurchase: purchase,
  };
}
